import { CSSProperties, FC, ReactNode, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled, { css, keyframes } from 'styled-components';
import isRelativeUrl from 'is-relative-url';

import { Campaign, GameResult } from '../../types';
import { GameWidgetWrapper, GameWrapper, GameFormWrapper, ResultScreen } from '../common';
import { BoxGameData, BoxInfo } from './types';
import { getWidgetBgDesktopImage, getWidgetBgMobileImage, getWixResizedImage, useAppSelector } from '../../lib';
import { Form } from '../common/form';

import './styles.css';
import { TApiError } from '../../components';

const Box: FC<{ box: BoxInfo; onClick: () => void; small: boolean }> = ({ box, onClick, small }) => {
  const imageUrl = isRelativeUrl(box.imageUrl) ? getWixResizedImage(box.imageUrl, 300, 300) : box.imageUrl;

  return <BoxContainer imageUrl={imageUrl} onClick={onClick} className="game-box" small={small} />;
};

const DesktopBoxes: FC<{ boxes: BoxInfo[]; onClick: () => void; small: boolean }> = ({ boxes, onClick, small }) => {
  return (
    <>
      <BoxesRow>
        <Box box={boxes[0]} onClick={onClick} small={small} />
        <Box box={boxes[1]} onClick={onClick} small={small} />
      </BoxesRow>
      <BoxesRow>
        <Box box={boxes[2]} onClick={onClick} small={small} />
      </BoxesRow>
      <BoxesRow>
        <Box box={boxes[3]} onClick={onClick} small={small} />
        <Box box={boxes[4]} onClick={onClick} small={small} />
      </BoxesRow>
    </>
  );
};

const MobileBoxes: FC<{ boxes: BoxInfo[]; onClick: () => void; small: boolean }> = ({ boxes, onClick, small }) => {
  return (
    <>
      <BoxesRow>
        <Box box={boxes[0]} onClick={onClick} small={small} />
        <Box box={boxes[1]} onClick={onClick} small={small} />
      </BoxesRow>
      <BoxesRow>
        <Box box={boxes[2]} onClick={onClick} small={small} />
      </BoxesRow>
    </>
  );
};

export type BoxesGameProps = {
  campaign: Campaign;
  gameData: BoxGameData;
  isDesktop?: boolean;
  boxesEditor?: ReactNode;
  formEditor?: ReactNode;
  resultScreenEditor?: ReactNode;
  gameResult?: GameResult;
  gameResultError?: TApiError;
  resultScreenVisible: boolean;
  onAnimationEnd: () => void;
  onBoxClick: () => void;
  onCloseResultScreen: () => void;
};

export const BoxesGame: FC<BoxesGameProps> = ({
  gameData,
  campaign,
  boxesEditor,
  formEditor,
  resultScreenEditor,
  gameResult,
  resultScreenVisible,
  gameResultError,
  onAnimationEnd,
  onBoxClick,
  onCloseResultScreen,
  ...restProps
}) => {
  const isMediaDesktop = useMediaQuery({ minWidth: 768 });
  const isDesktop = restProps.isDesktop ?? isMediaDesktop;
  const shouldReduceFieldsSize = useAppSelector((state) => state.config.reduceUISize);

  const onClose = useCallback(() => {
    onCloseResultScreen();
  }, [onCloseResultScreen]);

  const handleBoxClick = useCallback(() => {
    onBoxClick();
    onAnimationEnd();
  }, [onBoxClick, onAnimationEnd]);

  const boxes = isDesktop ? (
    <DesktopBoxes boxes={gameData.boxes.desktop} onClick={handleBoxClick} small={shouldReduceFieldsSize} />
  ) : (
    <MobileBoxes boxes={gameData.boxes.mobile} onClick={handleBoxClick} small={shouldReduceFieldsSize} />
  );

  const getWidgetStyles = (): CSSProperties => {
    const props: CSSProperties = {
      backgroundColor: campaign.colors.bg,
    };

    if (campaign.useBgImage) {
      let bgImageUrl = isDesktop
        ? getWidgetBgDesktopImage(campaign.bgImageUrl.desktop)
        : getWidgetBgMobileImage(campaign.bgImageUrl.mobile || campaign.bgImageUrl.desktop);

      props.backgroundImage = `url(${
        isDesktop ? getWidgetBgDesktopImage(bgImageUrl) : getWidgetBgMobileImage(bgImageUrl)
      })`;
    }

    return props;
  };

  return (
    <GameWidgetWrapper style={getWidgetStyles()}>
      {!resultScreenVisible && (
        <>
          <GameWrapper>
            <BoxesWrapper
              /** className не удалять, используется в ./styles.css */
              className="boxes-wrapper"
            >
              {boxesEditor || boxes}
            </BoxesWrapper>
          </GameWrapper>

          <GameFormWrapper>{formEditor || <Form gameResultError={gameResultError} />}</GameFormWrapper>
        </>
      )}

      {resultScreenEditor || (
        <ResultScreen
          visible={resultScreenVisible}
          gameResult={gameResult}
          onClose={onClose}
          gameResultError={gameResultError}
        />
      )}
    </GameWidgetWrapper>
  );
};

const BoxesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 12px;
  padding-top: 24px;

  @media screen and (min-width: 768px) {
    width: 620px;
    height: 100%;
    padding: 24px;
  }
`;

const BoxesRow = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  :last-child {
    margin-bottom: 0;
  }
`;

const pulseKeyframes = keyframes`
  25% {
    transform: scale(1.02);
  }

  75% {
    transform: scale(0.98);
  }
`;

const hoverKeyframes = keyframes`
  0% {
    transform: scale(0.98);
  }

  100% {
    transform: scale(1.2);
  }
`;

const BoxContainer = styled.div<{
  imageUrl: string;
  small: boolean;
}>`
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  background-image: ${(p) => `url(${p.imageUrl})`};
  animation: ${pulseKeyframes} 1s linear infinite;

  ${(p) =>
    p.small
      ? css`
          width: 110px;
          height: 110px;
        `
      : css`
          width: 140px;
          height: 140px;
        `};

  :hover {
    animation: ${hoverKeyframes} 0.3s linear 1 forwards;
  }

  @media screen and (min-width: 768px) {
    width: 140px;
    height: 140px;
  }
`;

export default BoxesGame;
